import { Box, Button, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import { queryWishListItems, queryPackingListItems, WishListItem } from '../schema';
import { useMemo, useState } from 'react';
import WishListItemRow from './WishListItemRow';
import AddWishListItemDialog from './AddWishListItemDialog';
import ListEmptyState from './ListEmptyState';

interface Props {
    eventId: string;
    onAddItem?: () => void;
}

export default function EventInventoryWishList({ eventId, onAddItem: _onAddItem }: Props) {
    const { data, isLoading, error } = queryWishListItems(eventId);
    const { data: packingData } = queryPackingListItems(eventId);
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const event = data?.events?.[0];
    const wishListItems = useMemo(() => (event?.wishListItems || []) as WishListItem[], [event?.wishListItems]);

    // Packing list quantity per item type (for negative wish list bound)
    const packingQuantityByItemType = useMemo(() => {
        const map = new Map<string, number>();
        const packingItems = packingData?.events?.[0]?.packingListItems ?? [];
        for (const item of packingItems as Array<{ itemType?: { id: string } | null; quantity?: number }>) {
            const id = item.itemType?.id;
            if (!id) continue;
            const qty = item.quantity ?? 0;
            map.set(id, (map.get(id) ?? 0) + qty);
        }
        return map;
    }, [packingData?.events]);

    // Sort items by name
    const sortedItems = useMemo(() => {
        return [...wishListItems].sort((a, b) => {
            const nameA = a.itemType?.name || '';
            const nameB = b.itemType?.name || '';
            return nameA.localeCompare(nameB);
        });
    }, [wishListItems]);

    if (isLoading) {
        return (
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Stack>
        );
    }

    if (error) {
        return (
            <Typography variant="body2" color="error" sx={{ py: 2 }}>
                Error loading wish list: {error?.message || 'Unknown error'}
            </Typography>
        );
    }

    return (
        <Box>
            {/* Section Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: 'warning.main' }} />
                    <Typography variant="body2" color="text.secondary">
                        Track items you wish you had for future planning
                    </Typography>
                </Box>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
                    Add Item
                </Button>
            </Box>

            <AddWishListItemDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                eventId={eventId}
                onItemAdded={() => {
                    // The query will automatically refresh
                    // Dialog stays open so user can add more items
                }}
            />

            {wishListItems.length === 0 ? (
                <ListEmptyState
                    icon={<StarIcon sx={{ fontSize: 48 }} />}
                    message="No wish list items yet"
                    subtext="Add items you wish you had brought for future planning"
                    primaryAction={{ label: 'Add Item', onClick: () => setAddDialogOpen(true) }}
                />
            ) : (
                <Paper
                    sx={{
                        overflowX: 'auto',
                        width: '100%',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        borderRadius: '8px',
                        border: '1px solid rgba(0,0,0,0.06)',
                        bgcolor: 'action.hover',
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Photo</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedItems.map(item => (
                                <WishListItemRow
                                    key={item.id}
                                    wishListItem={item}
                                    packingListQuantity={packingQuantityByItemType.get(item.itemType?.id ?? '') ?? 0}
                                    onRemove={() => {
                                        // Query will automatically refresh
                                    }}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
